import React, { useState, useEffect } from "react";
import {
  Phone,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  X,
  Plus,
  EyeOff,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../App";
import BossStatisticPlaces from "./BossStatisticComps/BossStatisticPlaces";
import BossCreatePlace from "./BossPlacesComps/BossCreatePlace";
import BossPlacesNotActive from "./BossPlacesComps/BossPlacesNotActive";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../AppContext";

const CONTACT_PHONE = "+972527477128";
const CONTACT_DISPLAY = "+972 52-747-7128";

export default function BossPlaces() {
  const { t } = useTranslation();
  const { Lang } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";
  const [modalVisible, setModalVisible] = useState(false);
  const [inactiveModalVisible, setInactiveModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [allData, setAllData] = useState(null);
  const [userData, setUserData] = useState(null);

  // Load from localStorage
  useEffect(() => {
    const user = localStorage.getItem("user");
    const data = localStorage.getItem("allData");
    if (user) setUserData(JSON.parse(user));
    if (data) setAllData(JSON.parse(data));
  }, []);

  // ── Subscription check ────────────────────────────────────────────────────
  const checkSubscriptionStatus = (dateStr) => {
    if (!dateStr) return;
    const [day, month, year] = dateStr.split("-");
    const expiryDate = new Date(year, month - 1, day);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil(
      (expiryDate - currentDate) / (1000 * 60 * 60 * 24),
    );
    const formattedDate = expiryDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    if (diffDays < 0) {
      setSubscriptionStatus({
        type: "expired",
        title: t("subscriptionExpired"),
        message: t("subscriptionExpiredMessage"),
        color: "#ef4444",
        icon: "❌",
      });
    } else if (diffDays === 0) {
      setSubscriptionStatus({
        type: "today",
        title: t("subscriptionExpiringToday"),
        message: t("subscriptionExpiringTodayMessage"),
        color: "#f97316",
        icon: "⚠️",
      });
    } else if (diffDays <= 7) {
      setSubscriptionStatus({
        type: "expiring-soon",
        title: t("subscriptionExpiringSoon"),
        message:
          diffDays === 1
            ? `Your subscription expires in 1 day (${formattedDate}).`
            : `Your subscription expires in ${diffDays} days (${formattedDate}).`,
        color: "#f59e0b",
        icon: "⏰",
        daysRemaining: diffDays,
      });
    } else {
      setSubscriptionStatus(null);
    }
  };

  const fetchData = async () => {
    if (!userData) return;
    setLoading(true);
    try {
      const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
        UserId: userData.id,
      });
      const data = response.data || {};
      setAllData(data);
      localStorage.setItem("allData", JSON.stringify(data));

      if (data.mySubscription && data.mySubscription.length > 0) {
        const last = [...data.mySubscription].sort((a, b) => b.id - a.id)[0];
        checkSubscriptionStatus(last.dateEnd);
      }
    } catch (err) {
      console.error("❌ Error fetching places:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData) fetchData();
  }, [userData]);

  // ── Contact actions ───────────────────────────────────────────────────────
  const getWhatsAppMessage = () => {
    if (!subscriptionStatus) return t("helloInquire");
    if (subscriptionStatus.type === "expired")
      return t("helloSubscriptionExpired");
    if (subscriptionStatus.type === "today")
      return t("helloSubscriptionExpiresToday");
    return t("helloSubscriptionExpiresSoon");
  };

  const openWhatsApp = () => {
    const phone = CONTACT_PHONE.replace("+", "");
    const msg = encodeURIComponent(getWhatsAppMessage());
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  const callPhone = () => {
    window.open(`tel:${CONTACT_PHONE}`);
  };

  const sendSMS = () => {
    const msg = encodeURIComponent(t("helloInquire"));
    window.open(`sms:${CONTACT_PHONE}?body=${msg}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isExpired = subscriptionStatus?.type === "expired";
  const isWarnBanner =
    subscriptionStatus?.type === "expiring-soon" ||
    subscriptionStatus?.type === "today";

  return (
    <div className="relative min-h-screen bg-gray-100">
      {/* ── Full banner (expired) ── */}
      {isExpired && (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div
            className="bg-white rounded-2xl shadow-md p-5 w-full max-w-lg border-l-4"
            style={{ borderLeftColor: subscriptionStatus.color }}
          >
            {/* Icon + title + message */}
            <div className="flex items-start gap-4 mb-5">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-2xl"
                style={{ backgroundColor: subscriptionStatus.color + "20" }}
              >
                {subscriptionStatus.icon}
              </div>
              <div className="flex-1">
                <p
                  className="text-lg font-bold mb-1"
                  style={{ color: subscriptionStatus.color }}
                >
                  {subscriptionStatus.title}
                </p>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {subscriptionStatus.message}
                </p>
              </div>
            </div>

            <div className="h-px bg-gray-200 mb-5" />

            <p className="text-sm font-semibold text-gray-700 mb-3">
              {t("contactUsToRenew")}
            </p>

            <div className="space-y-3">
              {/* Call */}
              <button
                onClick={callPhone}
                className="w-full flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl p-3.5 hover:bg-gray-100 transition"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-800">
                    {t("callUs")}
                  </p>
                  <p className="text-xs text-gray-500">{CONTACT_DISPLAY}</p>
                </div>
              </button>

              {/* WhatsApp */}
              <button
                onClick={openWhatsApp}
                className="w-full flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl p-3.5 hover:bg-gray-100 transition"
              >
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-green-500" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-800">
                    {t("chatOnWhatsApp")}
                  </p>
                  <p className="text-xs text-gray-500">{CONTACT_DISPLAY}</p>
                </div>
              </button>

              {/* SMS */}
              <button
                onClick={sendSMS}
                className="w-full flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl p-3.5 hover:bg-gray-100 transition"
              >
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-purple-500" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-800">
                    Send SMS
                  </p>
                  <p className="text-xs text-gray-500">{CONTACT_DISPLAY}</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Normal content (not expired) */}
      {!isExpired && (
        <>
          {/* ── Compact warning banner ── */}
          {isWarnBanner && (
            <button
              onClick={() => setShowContactModal(true)}
              className="w-full flex items-center gap-3 px-4 py-4 border-l-4 rounded-xl mx-4 mt-3 mb-2 text-left transition"
              style={{
                backgroundColor: subscriptionStatus.color + "18",
                borderLeftColor: subscriptionStatus.color,
                width: "calc(100% - 32px)",
              }}
            >
              <span className="text-2xl">{subscriptionStatus.icon}</span>
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-bold"
                  style={{ color: subscriptionStatus.color }}
                >
                  {subscriptionStatus.title}
                </p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  {subscriptionStatus.message}
                </p>
              </div>
              <ChevronDown
                className="w-5 h-5 flex-shrink-0"
                style={{ color: subscriptionStatus.color }}
              />
            </button>
          )}

          {/* Main content */}
          <BossStatisticPlaces onAddPlace={() => setModalVisible(true)} />

          {/* Create Place Modal */}
          <BossCreatePlace
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            onSuccess={() => {
              setModalVisible(false);
              fetchData();
            }}
          />

          {/* Inactive Places Modal */}
          <BossPlacesNotActive
            visible={inactiveModalVisible}
            onClose={() => setInactiveModalVisible(false)}
            onRefresh={fetchData}
          />

          {/* FABs */}
          <button
            onClick={() => setModalVisible(true)}
            className={`fixed bottom-6 ${isRTL ? "left-6" : "right-6"} rounded-full flex items-center justify-center shadow-xl transition z-40 bg-yellow-400 hover:bg-yellow-500`}
            style={{ width: 60, height: 60 }}
          >
            <Plus className="w-7 h-7 text-white" />
          </button>

          <button
            onClick={() => setInactiveModalVisible(true)}
            className={`fixed bottom-6 ${isRTL ? "left-24" : "right-24"} rounded-full flex items-center justify-center shadow-xl transition z-40 bg-red-500 hover:bg-red-600`}
            style={{ width: 56, height: 56 }}
          >
            <EyeOff className="w-6 h-6 text-white" />
          </button>
        </>
      )}

      {/* ── Contact bottom sheet (expiring soon / today) ── */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowContactModal(false)}
          />
          <div
            className="relative bg-white rounded-t-3xl w-full max-w-lg px-5 pt-2 pb-10 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" />

            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-slate-800">
                {t("contactToRenew")}
              </h3>
              <button
                onClick={() => setShowContactModal(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Phone display */}
            <div className="flex items-center justify-center gap-2.5 bg-slate-100 rounded-xl py-3 px-5 mb-6">
              <Phone className="w-5 h-5 text-sky-500" />
              <span className="text-base font-semibold text-slate-800">
                {CONTACT_DISPLAY}
              </span>
            </div>

            {/* Contact options */}
            <div className="space-y-3 mb-5">
              {/* Call */}
              <button
                onClick={() => {
                  callPhone();
                  setShowContactModal(false);
                }}
                className="w-full flex items-center gap-3 bg-sky-500 hover:bg-sky-600 rounded-xl p-4 transition"
              >
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-base font-semibold text-white">
                    {t("callUs")}
                  </p>
                  <p className="text-xs text-sky-100">{t("talkDirectly")}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-white" />
              </button>

              {/* WhatsApp */}
              <button
                onClick={() => {
                  openWhatsApp();
                  setShowContactModal(false);
                }}
                className="w-full flex items-center gap-3 rounded-xl p-4 transition"
                style={{ backgroundColor: "#25D366" }}
              >
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-base font-semibold text-white">
                    {t("chatOnWhatsApp")}
                  </p>
                  <p className="text-xs text-green-100">{t("quickResponse")}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-white" />
              </button>

              {/* SMS */}
              <button
                onClick={() => {
                  sendSMS();
                  setShowContactModal(false);
                }}
                className="w-full flex items-center gap-3 bg-purple-500 hover:bg-purple-600 rounded-xl p-4 transition"
              >
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-base font-semibold text-white">Send SMS</p>
                  <p className="text-xs text-purple-100">{t("sendMessage")}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>

            <p className="text-xs text-slate-400 text-center italic leading-relaxed">
              {t("weAreAvailable247")}
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
